import type { CSSProperties } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Masonry } from '@elfsight/lib-masonry-grid';
import { createReadableClass, useIsMobile } from '@elfsight/widget-utils';
import type { ButtonProps } from '../../../button';
import { LoadMoreButton } from '../LoadMoreButton';
import type {
  ColumnsModes,
  DataSourceProps,
  LayoutAPI,
  MappingFunction
} from '../types';
import { transformDataSource, useColumns, useLayoutData } from '../utils';
import { HeightObservableMasonryItem, MasonryItem } from './MasonryItem';

export type MasonryLayoutProps = {
  renderItem: MappingFunction;
  dataSource: DataSourceProps;
  columnsMode: ColumnsModes;
  itemSpacing: number;
  columnsDesktopLarge: number;
  columnsDesktopMedium: number;
  columnsDesktopSmall: number;
  columnsTablet: number;
  columnsMobile: number;
  columnBasis?: number;
  itemsPerPage?: number;
  itemsPerPageOnMobile?: number;
  loaderColor?: string;
  showLoadMoreButton?: boolean;
  loadMoreButtonText?: string;
  loadMoreButton?: ButtonProps;
  withPagination?: boolean;
  rerenderParent?: () => void;
  watchItemsHeights?: boolean;
  className?: string;
  onEffectiveColumnsChange?: (columnsCount: number) => void;
  rerender?: () => void;
};

export const MasonryLayout = forwardRef<LayoutAPI, MasonryLayoutProps>(
  (
    {
      renderItem,
      dataSource,
      columnsMode,
      columnsDesktopLarge,
      columnsDesktopMedium,
      columnsDesktopSmall,
      columnsTablet,
      columnsMobile,
      itemsPerPage,
      itemsPerPageOnMobile,
      itemSpacing,
      loaderColor,
      showLoadMoreButton,
      loadMoreButtonText,
      loadMoreButton,
      rerenderParent,
      watchItemsHeights,
      className,
      onEffectiveColumnsChange,
      rerender,
      columnBasis = 240,
      withPagination = false
    },
    forwardingRef
  ) => {
    const isMobile = useIsMobile();
    const dataSourceFormat = transformDataSource(dataSource);

    const {
      data: initialData,
      hasNext: hasNextFetch,
      next: handleNextFetch,
      status
    } = dataSourceFormat;

    const isLoading = status === 'pending';

    const {
      recalculateColumns,
      autoColumns,
      customColumns,
      windowWidth,
      handleEffectiveColumnsChange
    } = useColumns({
      columnsDesktopLarge,
      columnsDesktopMedium,
      columnsDesktopSmall,
      columnsTablet,
      columnsMobile
    });

    const columnsCount = columnsMode === 'auto' ? autoColumns : customColumns;

    const itemsCount = isMobile ? itemsPerPageOnMobile : itemsPerPage;

    const totalItemsPerPage: number =
      withPagination && itemsCount ? itemsCount : initialData?.length;

    const {
      data,
      hasNext,
      loadNext,
      reset: resetLayout
    } = useLayoutData({
      data: initialData,
      itemsCountPerPage: totalItemsPerPage,
      hasNextFetch,
      handleNextFetch
    });

    useImperativeHandle(forwardingRef, () => ({ resetLayout }), [resetLayout]);

    const effectiveColumnsChangeHandler = (columnsCount: number) => {
      onEffectiveColumnsChange?.(columnsCount);
      handleEffectiveColumnsChange(columnsCount);
    };

    useEffect(() => {
      recalculateColumns();
      if (rerenderParent) {
        rerenderParent();
      }
    }, [
      columnsMode,
      columnsDesktopLarge,
      columnsDesktopMedium,
      columnsDesktopSmall,
      columnsTablet,
      columnsMobile,
      itemsPerPageOnMobile,
      itemsPerPage,
      windowWidth,
      itemSpacing
    ]);

    useEffect(() => {
      resetLayout();
    }, [showLoadMoreButton]);

    useEffect(() => {
      rerender?.();
    }, [data.length]);

    const [updateHeights, setUpdateHeights] = useState(false);

    const itemInlineStyle = {
      transition: 'transform 0.4s ease'
    } as CSSProperties;

    const masonryContent = data?.map((value, index, array) => {
      const masonryItem = renderItem(value, index, array);
      const itemKey = masonryItem.key ?? index;

      if (watchItemsHeights) {
        return (
          <HeightObservableMasonryItem
            item={masonryItem}
            style={itemInlineStyle}
            key={itemKey}
            onHeightChange={() => {
              setUpdateHeights(!updateHeights);
            }}
          />
        );
      }

      return (
        <MasonryItem item={masonryItem} style={itemInlineStyle} key={itemKey} />
      );
    });

    return (
      <>
        <Masonry
          className={createReadableClass(
            {
              component: 'masonry-layout'
            },
            [className]
          )}
          columns={columnsMode === 'auto' ? 'auto' : columnsCount}
          basis={columnBasis}
          gap={itemSpacing}
          onEffectiveColumnsChange={effectiveColumnsChangeHandler}
          updateHeights={updateHeights}
        >
          {masonryContent}
        </Masonry>
        {withPagination && showLoadMoreButton && (
          <LoadMoreButton
            hasNext={hasNext}
            next={loadNext}
            isLoading={isLoading}
            text={loadMoreButtonText}
            gap={itemSpacing}
            loaderColor={loaderColor}
            loadMoreButton={loadMoreButton}
          />
        )}
      </>
    );
  }
);
